import React, { useEffect, useState, useCallback } from 'react';
import { FiTrash2, FiX, FiPlus, FiEdit2, FiChevronDown, FiChevronRight } from 'react-icons/fi';
import { getApiBaseUrl, authenticatedFetch } from '../utils/api';
import './CustomRulesPanel.css';

// Mirrors backend config.MAX_CUSTOM_RULE_CHARS / MAX_CUSTOM_RULE_TITLE_CHARS.
// Kept in sync manually since the frontend has no build-time access to
// backend/config.py; the backend re-enforces both regardless, so a drift
// here is only a UX inconvenience.
const MAX_RULE_CHARS = 1000;
const MAX_TITLE_CHARS = 60;

export const CUSTOM_RULES_UPDATED_EVENT = 'kai-custom-rules-updated';

const CustomRulesPanel = ({ onClose, isOpen }) => {
  const [rules, setRules] = useState([]);
  const [currentPolicyHash, setCurrentPolicyHash] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [newTitle, setNewTitle] = useState('');
  const [newText, setNewText] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState(null);
  const [createUnavailable, setCreateUnavailable] = useState(false);

  const [editingId, setEditingId] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const [editText, setEditText] = useState('');
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState(null);
  const [editUnavailable, setEditUnavailable] = useState(false);

  const [deletingId, setDeletingId] = useState(null);

  // Which rows are expanded to show their full (untruncated, multi-line)
  // text -- collapsed rows show a one-line ellipsis-truncated preview,
  // matching ConversationsSidebar.js's card style.
  const [expandedIds, setExpandedIds] = useState(() => new Set());

  const toggleExpanded = (ruleId) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(ruleId)) {
        next.delete(ruleId);
      } else {
        next.add(ruleId);
      }
      return next;
    });
  };

  // Non-blocking heads-up when a just-saved rule conflicts with one of the
  // user's OTHER saved rules (see backend custom_rules_routes.py's
  // `warning` field). Distinct from createError/editError, which are
  // blocking save failures -- this rule DID save, it's just a heads-up that
  // it and another rule shouldn't be attached to the same conversation.
  const [conflictWarning, setConflictWarning] = useState(null);

  // On failure this sets `error` and leaves `rules` untouched, so the empty
  // state ("You haven't added any rules yet") is only ever shown for a
  // genuinely empty library -- never for a read that failed. The backend
  // returns 503 rather than an empty list for exactly this reason (see
  // custom_rules_service.py's RuleFetchFailed). The raw response body is
  // logged, never rendered: it can carry backend detail that shouldn't reach
  // the browser, the same reason save errors return a fixed message.
  const fetchRules = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await authenticatedFetch(`${getApiBaseUrl()}/custom-rules`);
      if (!res.ok) {
        console.error('Failed to load custom rules:', res.status, await res.text());
        setError("Couldn't load your rules right now. Please try again.");
        return;
      }
      const body = await res.json();
      setRules(body.rules || []);
      setCurrentPolicyHash(body.current_policy_hash || null);
    } catch (err) {
      console.error('Error loading custom rules:', err);
      setError("Couldn't load your rules right now. Please try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    fetchRules();
  }, [isOpen, fetchRules]);

  const notifyUpdated = () => {
    window.dispatchEvent(new Event(CUSTOM_RULES_UPDATED_EVENT));
  };

  // Two distinct failure presentations. 422 means either a field is invalid
  // (empty/too long) or validate_custom_rule found a real conflict with
  // KAI's policies: red, keep the text so the user can revise it in place,
  // and the backend already put the specific reason in `detail`. Anything
  // else (503 validator-unavailable, or a 500 from the DynamoDB write itself
  // failing) is NOT a judgment on the rule's content, so it gets the neutral
  // "couldn't save" treatment with a Retry button instead -- for a 500,
  // `detail` is a deliberately generic "temporarily unavailable" message
  // (the real cause is logged server-side only, never returned to the
  // client, see custom_rules_routes.py's _log_and_get_generic_error_detail).
  const parseSaveError = async (res) => {
    let detail = 'Something went wrong saving this rule.';
    try {
      const body = await res.json();
      if (body?.detail) detail = body.detail;
    } catch {
      // ignore parse failure, use default message
    }
    return { detail, unavailable: res.status !== 422 };
  };

  const handleCreate = async () => {
    const title = newTitle.trim();
    const text = newText.trim();
    if (!title || !text || creating) return;
    setCreating(true);
    setCreateError(null);
    setCreateUnavailable(false);
    setConflictWarning(null);
    try {
      const res = await authenticatedFetch(`${getApiBaseUrl()}/custom-rules`, {
        method: 'POST',
        body: JSON.stringify({ title, text }),
      });
      if (!res.ok) {
        const { detail, unavailable } = await parseSaveError(res);
        setCreateError(detail);
        setCreateUnavailable(unavailable);
        return;
      }
      const body = await res.json();
      setRules((prev) => [body.rule, ...prev]);
      setNewTitle('');
      setNewText('');
      if (body.warning) {
        setConflictWarning({
          conflictingTitle: body.warning.conflicting_rule_title,
          reason: body.warning.reason,
        });
      }
      notifyUpdated();
    } catch (err) {
      console.error('Error creating custom rule:', err);
      setCreateError('Something went wrong saving this rule.');
      setCreateUnavailable(true);
    } finally {
      setCreating(false);
    }
  };

  const startEdit = (rule) => {
    setEditingId(rule.rule_id);
    setEditTitle(rule.title || '');
    setEditText(rule.text);
    setEditError(null);
    setEditUnavailable(false);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditTitle('');
    setEditText('');
    setEditError(null);
    setEditUnavailable(false);
  };

  const handleSaveEdit = async (ruleId) => {
    const title = editTitle.trim();
    const text = editText.trim();
    if (!title || !text || editSaving) return;
    setEditSaving(true);
    setEditError(null);
    setEditUnavailable(false);
    setConflictWarning(null);
    try {
      const res = await authenticatedFetch(`${getApiBaseUrl()}/custom-rules/${ruleId}`, {
        method: 'PUT',
        body: JSON.stringify({ title, text }),
      });
      if (!res.ok) {
        const { detail, unavailable } = await parseSaveError(res);
        setEditError(detail);
        setEditUnavailable(unavailable);
        return;
      }
      const body = await res.json();
      setRules((prev) => prev.map((r) => (r.rule_id === ruleId ? body.rule : r)));
      if (body.warning) {
        setConflictWarning({
          conflictingTitle: body.warning.conflicting_rule_title,
          reason: body.warning.reason,
        });
      }
      notifyUpdated();
      cancelEdit();
    } catch (err) {
      console.error('Error updating custom rule:', err);
      setEditError('Something went wrong saving this rule.');
      setEditUnavailable(true);
    } finally {
      setEditSaving(false);
    }
  };

  const handleDelete = async (ruleId) => {
    if (!window.confirm('Delete this rule? It will be detached from any conversation using it.')) return;
    setDeletingId(ruleId);
    try {
      const res = await authenticatedFetch(`${getApiBaseUrl()}/custom-rules/${ruleId}`, {
        method: 'DELETE',
      });
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(`Failed to delete rule (${res.status}): ${txt}`);
      }
      setRules((prev) => prev.filter((r) => r.rule_id !== ruleId));
      notifyUpdated();
    } catch (err) {
      console.error('Error deleting custom rule:', err);
      alert('Failed to delete rule');
    } finally {
      setDeletingId(null);
    }
  };

  const isStale = (rule) =>
    !!(rule.validated_policy_hash && currentPolicyHash && rule.validated_policy_hash !== currentPolicyHash);

  // Case-insensitive, whitespace-trimmed duplicate check against the
  // already-loaded rules list -- purely client-side, no extra request, so it
  // can update on every keystroke. The backend re-checks this itself (see
  // custom_rules_routes.py's _duplicate_title_or_none) so a stale client-side
  // `rules` list can never let a real duplicate slip through, only delay the
  // warning until submit.
  const findDuplicateTitle = (title, excludeRuleId = null) => {
    const normalized = title.trim().toLowerCase();
    if (!normalized) return null;
    const match = rules.find(
      (r) => r.rule_id !== excludeRuleId && r.title.trim().toLowerCase() === normalized
    );
    return match ? match.title : null;
  };

  const newTitleDuplicate = findDuplicateTitle(newTitle);
  const editTitleDuplicate = editingId ? findDuplicateTitle(editTitle, editingId) : null;

  return (
    <div className="custom-rules-panel">
      <div className="custom-rules-header">
        <div className="custom-rules-header-content">
          <h3>Your custom rules for KAI</h3>
        </div>
        <button
          className="close-sidebar-btn"
          onClick={onClose}
          title="Close custom rules panel"
          aria-label="Close custom rules panel"
        >
          <FiX size={16} />
        </button>
      </div>

      <div className="custom-rules-body">
        <p className="custom-rules-intro">
          Tell KAI how to behave or teach it a skill of your own. Title it, write
          it, and attach it from the icon next to the paperclip -- it stays
          attached when you come back to that conversation. KAI's own policies
          always win over a rule.
        </p>

        <div className="custom-rules-new">
          <input
            type="text"
            className="custom-rules-title-input"
            placeholder="Title (e.g. Writing assistant)"
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            maxLength={MAX_TITLE_CHARS}
            disabled={creating}
          />
          {newTitleDuplicate && (
            <div className="custom-rules-duplicate-title-warning">
              You already have a rule named &quot;{newTitleDuplicate}&quot;. Choose a different title.
            </div>
          )}
          <textarea
            className="custom-rules-textarea"
            placeholder="e.g. Be my writing assistant. Write my content in natural, humanized sentences, not robotic ones."
            value={newText}
            onChange={(e) => setNewText(e.target.value)}
            disabled={creating}
          />
          <div className="custom-rules-new-footer">
            <span className={`custom-rules-char-count${newText.length > MAX_RULE_CHARS ? ' over' : ''}`}>
              {newText.length}/{MAX_RULE_CHARS}
            </span>
            <button
              type="button"
              className="custom-rules-add-btn"
              onClick={handleCreate}
              disabled={
                !newTitle.trim() || !newText.trim()
                || newTitle.length > MAX_TITLE_CHARS || newText.length > MAX_RULE_CHARS
                || !!newTitleDuplicate
                || creating
              }
            >
              {creating ? 'Checking your rule…' : (<><FiPlus size={14} /> Add rule</>)}
            </button>
          </div>
          {newText.length > MAX_RULE_CHARS && (
            <div className="custom-rules-length-warning">
              {newText.length - MAX_RULE_CHARS} character{newText.length - MAX_RULE_CHARS === 1 ? '' : 's'} over
              the {MAX_RULE_CHARS} limit -- shorten it before saving.
            </div>
          )}
          {createError && (
            <div className={`custom-rules-save-error${createUnavailable ? ' unavailable' : ''}`}>
              {createError}
              {createUnavailable && (
                <button type="button" className="custom-rules-retry-btn" onClick={handleCreate}>
                  Retry
                </button>
              )}
            </div>
          )}
        </div>

        {conflictWarning && (
          <div className="custom-rules-conflict-warning">
            {/* The backend supplies a one-sentence `reason` explaining HOW the
                two rules contradict each other. Without it the user is told
                two rules conflict but not why, which makes the heads-up hard
                to act on -- it's optional only because find_conflicting_rule
                fails open and may not always produce one. */}
            <span>
              Saved. Heads up: this conflicts with &quot;{conflictWarning.conflictingTitle}&quot;
              {conflictWarning.reason ? ` -- ${conflictWarning.reason}` : ''}
              {' '}Avoid attaching both to the same chat.
            </span>
            <button
              type="button"
              className="custom-rules-conflict-dismiss"
              onClick={() => setConflictWarning(null)}
              aria-label="Dismiss"
            >
              <FiX size={14} />
            </button>
          </div>
        )}

        {loading && <div className="custom-rules-loading">Loading rules…</div>}

        {error && (
          <div className="custom-rules-error">
            <p>{error}</p>
            <button onClick={fetchRules} className="custom-rules-retry-btn">Retry</button>
          </div>
        )}

        {!loading && !error && rules.length === 0 && (
          <div className="custom-rules-empty-state">
            <p>You haven&apos;t added any rules yet. Rules you add here can be attached to any conversation from the chat input.</p>
          </div>
        )}

        {!loading && !error && rules.length > 0 && (
          <ul className="custom-rules-list">
            {rules.map((rule) => {
              const isEditing = editingId === rule.rule_id;
              return (
                <li key={rule.rule_id} className="custom-rules-row">
                  {isEditing ? (
                    <div className="custom-rules-edit">
                      <input
                        type="text"
                        className="custom-rules-title-input"
                        placeholder="Title"
                        value={editTitle}
                        onChange={(e) => setEditTitle(e.target.value)}
                        maxLength={MAX_TITLE_CHARS}
                        disabled={editSaving}
                        autoFocus
                      />
                      {editTitleDuplicate && (
                        <div className="custom-rules-duplicate-title-warning">
                          You already have a rule named &quot;{editTitleDuplicate}&quot;. Choose a different title.
                        </div>
                      )}
                      <textarea
                        className="custom-rules-textarea"
                        value={editText}
                        onChange={(e) => setEditText(e.target.value)}
                        disabled={editSaving}
                      />
                      <div className="custom-rules-new-footer">
                        <span className={`custom-rules-char-count${editText.length > MAX_RULE_CHARS ? ' over' : ''}`}>
                          {editText.length}/{MAX_RULE_CHARS}
                        </span>
                        <div className="custom-rules-edit-actions">
                          <button
                            type="button"
                            className="custom-rules-cancel-btn"
                            onClick={cancelEdit}
                            disabled={editSaving}
                          >
                            Cancel
                          </button>
                          <button
                            type="button"
                            className="custom-rules-add-btn"
                            onClick={() => handleSaveEdit(rule.rule_id)}
                            disabled={
                              !editTitle.trim() || !editText.trim()
                              || editTitle.length > MAX_TITLE_CHARS || editText.length > MAX_RULE_CHARS
                              || !!editTitleDuplicate
                              || editSaving
                            }
                          >
                            {editSaving ? 'Checking your rule…' : 'Save'}
                          </button>
                        </div>
                      </div>
                      {editText.length > MAX_RULE_CHARS && (
                        <div className="custom-rules-length-warning">
                          {editText.length - MAX_RULE_CHARS} character{editText.length - MAX_RULE_CHARS === 1 ? '' : 's'} over
                          the {MAX_RULE_CHARS} limit -- shorten it before saving.
                        </div>
                      )}
                      {editError && (
                        <div className={`custom-rules-save-error${editUnavailable ? ' unavailable' : ''}`}>
                          {editError}
                          {editUnavailable && (
                            <button
                              type="button"
                              className="custom-rules-retry-btn"
                              onClick={() => handleSaveEdit(rule.rule_id)}
                            >
                              Retry
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  ) : (
                    <>
                      <div
                        className="custom-rules-row-text"
                        onClick={() => toggleExpanded(rule.rule_id)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            toggleExpanded(rule.rule_id);
                          }
                        }}
                        role="button"
                        tabIndex={0}
                        aria-expanded={expandedIds.has(rule.rule_id)}
                      >
                        <div className="custom-rules-row-title-line">
                          {expandedIds.has(rule.rule_id) ? (
                            <FiChevronDown size={14} className="custom-rules-row-chevron" />
                          ) : (
                            <FiChevronRight size={14} className="custom-rules-row-chevron" />
                          )}
                          <div className="custom-rules-row-title">{rule.title}</div>
                        </div>
                        <div className={`custom-rules-row-body${expandedIds.has(rule.rule_id) ? ' expanded' : ''}`}>
                          {rule.text}
                        </div>
                        {isStale(rule) && (
                          <div className="custom-rules-stale-hint">
                            Not re-checked since KAI&apos;s policies last changed.
                          </div>
                        )}
                      </div>
                      <div className="custom-rules-row-actions">
                        <button
                          type="button"
                          className="custom-rules-icon-btn"
                          onClick={() => startEdit(rule)}
                          title="Edit rule"
                          aria-label="Edit rule"
                        >
                          <FiEdit2 size={14} />
                        </button>
                        <button
                          type="button"
                          className="custom-rules-icon-btn"
                          onClick={() => handleDelete(rule.rule_id)}
                          disabled={deletingId === rule.rule_id}
                          title="Delete rule"
                          aria-label="Delete rule"
                        >
                          <FiTrash2 size={14} />
                        </button>
                      </div>
                    </>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};

export default CustomRulesPanel;
